import React, { useEffect } from 'react';

const sizeMap = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-5xl',
  '3xl': 'max-w-6xl',
  full: 'max-w-[96vw]',
};

/**
 * Modal — accessible dialog with backdrop blur, animated entrance,
 * gradient header and optional footer. Designed to be a drop-in
 * replacement for inline modal markup throughout the app.
 *
 * The scrollable body has default horizontal padding (`px-6`) so form fields
 * never sit flush against the dialog edge. Pass `bodyClassName="!p-0"` (or
 * adjust padding) only for rare full-bleed layouts.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  icon,
  size = 'md',
  children,
  footer,
  hideHeader = false,
  closeOnBackdrop = true,
  showCloseButton = true,
  className = '',
  bodyClassName = '',
  headerVariant = 'gradient', // 'gradient' | 'plain'
  /** When `title` is not a plain string, set this for the dialog `aria-label`. */
  ariaLabel,
}) {
  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open || !onClose) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdrop = () => {
    if (closeOnBackdrop && onClose) onClose();
  };

  const headerClass =
    headerVariant === 'gradient'
      ? 'px-6 py-4 bg-gradient-to-r from-primary-800 via-primary-700 to-secondary-700 text-white'
      : 'px-6 py-4 bg-white border-b border-slate-100 text-slate-900';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 bg-slate-900/55 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
    >
      <div
        className={`
          relative w-full ${sizeMap[size] || sizeMap.md}
          bg-white rounded-2xl shadow-large border border-slate-200/80
          ring-1 ring-slate-900/5 overflow-hidden
          animate-scale-in
          max-h-[92vh] flex flex-col
          ${className}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {!hideHeader && (
          <div className={`${headerClass} flex items-center justify-between gap-4 shrink-0`}>
            <div className="min-w-0 flex items-center gap-3">
              {icon && (
                <span className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                  headerVariant === 'gradient' ? 'bg-white/15 text-white' : 'bg-primary-50 text-primary-700'
                }`}>
                  {icon}
                </span>
              )}
              <div className="min-w-0">
                {title && (
                  <h3
                    className={`${
                      headerVariant === 'gradient'
                        ? typeof title === 'string'
                          ? 'truncate text-xl font-light tracking-tight text-white sm:text-2xl'
                          : 'min-w-0 text-white'
                        : `text-lg sm:text-xl font-semibold tracking-tight ${
                            typeof title === 'string' ? 'truncate' : 'min-w-0'
                          } text-slate-900`
                    }`}
                  >
                    {title}
                  </h3>
                )}
                {description && (
                  <p
                    className={`text-xs sm:text-sm mt-0.5 line-clamp-2 leading-snug ${
                      headerVariant === 'gradient' ? 'text-white/80' : 'text-slate-500'
                    }`}
                  >
                    {description}
                  </p>
                )}
              </div>
            </div>
            {showCloseButton && onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close dialog"
                className={`shrink-0 p-1.5 rounded-lg transition-colors ${
                  headerVariant === 'gradient'
                    ? 'text-white hover:bg-white/15'
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div
          className={`flex-1 overflow-y-auto px-6 py-4 sm:py-5 min-h-0 ${bodyClassName}`.trim()}
        >
          {children}
        </div>

        {footer && (
          <div className="px-6 py-4 bg-slate-50/60 border-t border-slate-100 flex items-center justify-end gap-3 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
